"use client";

import { CelebrityAvatar } from "@/components/celebrity-avatar";
import { CelebrityFollowButton } from "@/components/celebrity-follow-button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Sparkles } from "lucide-react";
import { trackCelebrityClick, trackBaikeClick, trackWeiboSearch } from "@/lib/analytics";

interface CelebrityExtensionProps {
  name: string;
  category?: string;
  avatarUrl?: string;
  baikeUrl?: string;
  /** 头像尺寸,默认 md */
  avatarSize?: "xs" | "sm" | "md";
  avatarShape?: "circle" | "portrait";
}

/**
 * 名人拓展展示: 头像 + 名字 + 分类 + 百度百科 + 微博搜索外链。
 * 纯展示组件(server/client 通用),用于词条详情独立页和抽屉。
 */
export function CelebrityExtension({
  name,
  category,
  avatarUrl,
  baikeUrl,
  avatarSize = "md",
  avatarShape = "portrait",
}: CelebrityExtensionProps) {
  const weiboUrl = `https://s.weibo.com/weibo?q=${encodeURIComponent(name)}`;
  const href = `/celebrity/${encodeURIComponent(name)}`;

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-gradient-to-br from-background to-muted/30 p-3">
      <a
        href={href}
        onClick={() => trackCelebrityClick(name, "extension_card")}
        className="shrink-0"
        title={`查看 ${name} 的名人页`}
      >
        <CelebrityAvatar
          name={name}
          src={avatarUrl}
          size={avatarSize}
          shape={avatarShape}
          className={avatarShape === "portrait" ? "h-16 w-12 ring-2 ring-background shadow-sm" : undefined}
        />
      </a>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-pink-500" />
          <a
            href={href}
            onClick={() => trackCelebrityClick(name, "extension_card")}
            className="truncate font-semibold hover:text-primary"
          >
            {name}
          </a>
          {category && (
            <Badge variant="secondary" className="shrink-0 text-xs">
              {category}
            </Badge>
          )}
        </div>
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
          已识别为相关明星动态，可进入专属页查看近期热搜与讨论走势。关注后可提升该明星的追踪优先级。
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <CelebrityFollowButton name={name} compact />
          {baikeUrl && (
            <a
              href={baikeUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBaikeClick("celebrity_extension", name)}
              className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <ExternalLink className="h-3 w-3" />
              百度百科
            </a>
          )}
          <a
            href={weiboUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWeiboSearch("celebrity_extension", name)}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <ExternalLink className="h-3 w-3" />
            微博搜索
          </a>
        </div>
      </div>
    </div>
  );
}
